import type { DatumEntity } from "building-types/datums/datumEntityTypes";
import { DatumDirection, DatumOffsetType, DatumType } from "building-types/datums/datumEntityTypes";
import {
  HorizontalJustification,
  LevelDatumAnchor,
  RelativeVerticalDatum,
  VerticalJustification,
  WallHorizontalDatum,
  WallVerticalDatumAnchor,
} from "building-types/datums/datumsTypesEnum";
import { SpanExtrema } from "building-types/facades/facadeTypesEnum";
import type { Table } from "common/data/table";

export const STACKED_LEVEL_DATUM_ID = "stackedLevelDatum";

// built-in datums
export const builtInDatums: Table<DatumEntity> = {
  // Legacy Level Datums
  [LevelDatumAnchor.LEVEL_FLOOR_ELEVATION]: {
    entityType: "DATUM",
    id: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
    sortIndex: 0,
    name: "Level floor elevation",
    datumType: DatumType.LEVEL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
    justification: VerticalJustification.BOTTOM_UP,
    offsetNormalized: false,
  },
  [LevelDatumAnchor.LEVEL_TOP_OF_WALL]: {
    entityType: "DATUM",
    id: LevelDatumAnchor.LEVEL_TOP_OF_WALL,
    sortIndex: 1,
    name: "Level top of wall",
    datumType: DatumType.LEVEL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: LevelDatumAnchor.LEVEL_TOP_OF_WALL,
    justification: VerticalJustification.BOTTOM_UP,
    offsetNormalized: false,
  },
  // Legacy Facade Vertical Datums
  [SpanExtrema.BASE_WALL_BOTTOM]: {
    entityType: "DATUM",
    id: SpanExtrema.BASE_WALL_BOTTOM,
    sortIndex: 0,
    name: "Base wall bottom",
    datumType: DatumType.WALL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: WallVerticalDatumAnchor.BASE_WALL,
    justification: VerticalJustification.BOTTOM_UP,
    offsetNormalized: false,
  },
  [SpanExtrema.BASE_WALL_TOP]: {
    entityType: "DATUM",
    id: SpanExtrema.BASE_WALL_TOP,
    sortIndex: 1,
    name: "Base wall top",
    datumType: DatumType.WALL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: WallVerticalDatumAnchor.BASE_WALL,
    justification: VerticalJustification.TOP_DOWN,
    offsetNormalized: false,
  },
  [SpanExtrema.GABLE_END_BOTTOM]: {
    entityType: "DATUM",
    id: SpanExtrema.GABLE_END_BOTTOM,
    sortIndex: 2,
    name: "Gable end bottom",
    datumType: DatumType.WALL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: WallVerticalDatumAnchor.GABLE_END,
    justification: VerticalJustification.BOTTOM_UP,
    offsetNormalized: false,
  },
  [SpanExtrema.GABLE_END_TOP]: {
    entityType: "DATUM",
    id: SpanExtrema.GABLE_END_TOP,
    sortIndex: 3,
    name: "Gable end top",
    datumType: DatumType.WALL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: WallVerticalDatumAnchor.GABLE_END,
    justification: VerticalJustification.TOP_DOWN,
    offsetNormalized: false,
  },
  [SpanExtrema.WALL_START]: {
    entityType: "DATUM",
    id: SpanExtrema.WALL_START,
    sortIndex: 4,
    name: "Wall start",
    datumType: DatumType.WALL,
    direction: DatumDirection.HORIZONTAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: WallHorizontalDatum.OUTSIDE_OF_SURFACE,
    justification: HorizontalJustification.START_TO_END,
    offsetNormalized: false,
  },
  [SpanExtrema.WALL_END]: {
    entityType: "DATUM",
    id: SpanExtrema.WALL_END,
    sortIndex: 5,
    name: "Wall end",
    datumType: DatumType.WALL,
    direction: DatumDirection.HORIZONTAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    anchor: WallHorizontalDatum.OUTSIDE_OF_SURFACE,
    justification: HorizontalJustification.END_TO_START,
    offsetNormalized: false,
  },
};

/**
 * We only need to do this for the finish layers not created by an override,
 * so that we generate a face that encompasses the whole substrate domain.
 * @TODO - remove after all wall assembly finish layers are mapped to the wall domain
 */

export enum HackStartDatumIdsUntilAllFacesAreRemapped {
  WALL_START = "hackStartDatumUntilAllFacesAreRemapped",
  WALL_END = "hackEndDatumUntilAllFacesAreRemapped",
}

export const hackStartDatumsUntilAllFacesAreRemapped: Table<DatumEntity> = {
  [HackStartDatumIdsUntilAllFacesAreRemapped.WALL_START]: {
    entityType: "DATUM",
    id: HackStartDatumIdsUntilAllFacesAreRemapped.WALL_START,
    sortIndex: 0,
    name: "Hack wall start",
    datumType: DatumType.WALL,
    direction: DatumDirection.HORIZONTAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: Number.NEGATIVE_INFINITY,
    anchor: WallHorizontalDatum.OUTSIDE_OF_SURFACE,
    justification: HorizontalJustification.START_TO_END,
    offsetNormalized: true,
  },
  [HackStartDatumIdsUntilAllFacesAreRemapped.WALL_END]: {
    entityType: "DATUM",
    id: HackStartDatumIdsUntilAllFacesAreRemapped.WALL_END,
    sortIndex: 1,
    name: "Hack wall end",
    datumType: DatumType.WALL,
    direction: DatumDirection.HORIZONTAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: Number.NEGATIVE_INFINITY,
    anchor: WallHorizontalDatum.OUTSIDE_OF_SURFACE,
    justification: HorizontalJustification.END_TO_START,
    offsetNormalized: true,
  },
};

export const stackedLevelDatums: Table<DatumEntity> = {
  [STACKED_LEVEL_DATUM_ID]: {
    entityType: "DATUM",
    id: STACKED_LEVEL_DATUM_ID,
    sortIndex: 6,
    name: "Stacked level datum",
    datumType: DatumType.STACKED_LEVEL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    offset: 0,
    justification: VerticalJustification.BOTTOM_UP,
    offsetNormalized: false,
  },
};

export const builtInDatumsGroupId = "builtInDatumsGroup";

export const isStairSpecificVerticalDatum: {
  [K in RelativeVerticalDatum]?: boolean;
} = {
  [RelativeVerticalDatum.STAIR_SYSTEM]: true,
  [RelativeVerticalDatum.BOTTOM_OF_STAIR_SYSTEM_HOST]: true,
};

export const isLevelSpecificVerticalDatum: {
  [K in RelativeVerticalDatum]?: boolean;
} = {
  [RelativeVerticalDatum.TOP_OF_FF_HOST]: true,
  [RelativeVerticalDatum.TOP_OF_WALL_PLATE_HOST]: true,
  [RelativeVerticalDatum.TOP_OF_FF_ABOVE_HOST]: true,
  [RelativeVerticalDatum.TOP_OF_WALL_PLATE_ABOVE_HOST]: true,
  [RelativeVerticalDatum.TOP_OF_FF_FINISH_BELOW_HOST]: true,
  [RelativeVerticalDatum.TOP_OF_WALL_PLATE_BELOW_HOST]: true,
};

export function isBuiltInDatum(datumId: string): boolean {
  return builtInDatums[datumId] !== undefined;
}

export const builtInAndLevelSpecificDatumIds = new Set([
  ...Object.keys(builtInDatums),
  ...Object.keys(isLevelSpecificVerticalDatum),
  ...Object.keys(isStairSpecificVerticalDatum),
]);
